import React, { useState } from 'react';
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { useTranslation } from 'react-i18next';
import { FlowTask, flowTasksService } from "@/app/providers/services";
import styled from '@emotion/styled';

import { EmptyContent, scrollable } from '@/common';
import { PhasePanel } from '@/components/InteractionsDropdown';

import { decisionGraphInteractionsByPhase } from '@imminently/decisively-core';
import { useSessionDecisionGraph } from '../sessionGraph';
import { useFullRelease } from '@/common/hooks_useFullRelease';

import { FlowTaskForm } from '../SessionTaskModal';
import { TaskList, useFlowTasks } from './TaskList';
import Icon from "@imminently/decisively-ui/Icon";

const Pane = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-template-rows: max-content;
  gap: 0.5rem;
  height: 100%;
  align-items: stretch;
  padding: 0.5rem;
  background-color: var(--subtle-light);

  #interactions {
    // span 4 columns
    grid-column: span 4;
    background-color: var(--background);
    border-radius: 0.25rem;
    border: 1px solid var(--border);
  }

  #task-list {
    grid-column: span 1;
    border-radius: 0.25rem;
  }

  #task-form {
    grid-column: span 3;
    background-color: var(--background);
    border-radius: 0.25rem;
    border: 1px solid var(--border);
  }
`;

// legacy - use if tasks can be made from interaction list to save a BE call
const TaskPanel = ({ taskId }: { taskId: string; }) => {
  const { t } = useTranslation();
  const { data: task, isError, isLoading } = flowTasksService.useGetOne(taskId);
  if (isLoading) return <LoadingSpinner size="sm"  />;
  if (isError) return <EmptyContent messages={["Failed to load task"]} img="" />;
  return <FlowTaskForm task={task as FlowTask} close={() => { }} />;
};

const useInteractions = (session: any) => {
  const graph = useSessionDecisionGraph();
  const release = useFullRelease();

  // walked over the decision graph's ids from the goal, once per session graph
  const res = React.useMemo(
    () =>
      graph && session.goal !== undefined
        ? decisionGraphInteractionsByPhase(session, graph, release ?? undefined)
        : ({} as Record<string, { interactions: any[] }>),
    [graph, session, release],
  );

  // requires a goal
  if (session.goal === undefined) return [];
  const getPhaseTitle = (phase: string) => phase === "_nophase" ? "Unassigned" : phase;
  // result is an object of phase names, with an array of interactions
  // convert into array of objects with phase name and interactions
  return Object.keys(res)
    .map((phase, i) => ({
      id: `phase-${i}`,
      title: getPhaseTitle(phase),
      tasks: res[phase].interactions
    }))
    .filter(p => p.tasks.length > 0)
    .map((p, i) => ({
      ...p,
      order: i + 1, // this might end up being custom order
    }));
};

export const InteractionsPanel = ({ session }: { session: any }) => {
  const [selected, setSelected] = useState<FlowTask | null>(null);
  const { data: tasks, query, total, loadMore } = useFlowTasks({ session: session.id });
  const { isLoading } = query;
  const canLoad = !query.isError && !query.isFetching && (query.hasNextPage ?? false);

  const onTaskClick = (task: FlowTask) => {
    setSelected(task);
  };

  const onTaskClose = () => {
    setSelected(null);
  };

  const [interactionsExpanded, setInteractionsExpanded] = useState(false);
  const phases = useInteractions(session);
  console.log('interactions by phase', phases);

  return (
    <Pane>
      {
        phases && phases.length > 0 ? (
          <PhasePanel
            id="interactions"
            expanded={interactionsExpanded}
            toggleExpanded={() => setInteractionsExpanded(prev => !prev)}
            phases={phases}
          />
        ) : <EmptyContent id="interactions" messages={["No phase data"]} />
      }
      <TaskList
        id="task-list"
        tasks={tasks as FlowTask[]}
        total={total}
        isLoading={isLoading}
        canLoad={canLoad}
        loadMore={loadMore}
        onTaskClick={onTaskClick}
        activeId={selected ? selected.id : undefined}
      />
      {
        selected
          ? (
            <div id="task-form" className={`flex flex-col ${scrollable}`}>
              {/* make sure we assign a key, otherwise it won't re-render */}
              <FlowTaskForm key={selected.id} task={selected} close={onTaskClose} />
            </div>
          )
          : <EmptyContent id="task-form" messages={["No task selected"]} />
      }
    </Pane>
  );
};
